import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Product } from '../types';
import { productService } from '../services/productService';

interface OrderInfo {
  orderId?: string;
  email?: string;
  sessionId?: string;
  verified?: boolean;
}

const Download: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productService.getProduct(id!);
      setProduct(data);
    } catch (err) {
      console.error('Error fetching product:', err);
      navigate('/products');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      // 检查用户是否已登录
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        // 未登录，跳转到登录页面
        navigate('/login', { state: { from: `/download/${id}` } });
        return;
      }
      
      fetchProduct();
      
      // 获取订单信息
      const orderId = searchParams.get('order_id');
      const sessionId = searchParams.get('session_id');
      const email = searchParams.get('email');
      
      if ((orderId || sessionId) && email) {
        setOrderInfo({ 
          orderId: (orderId || sessionId) || undefined, 
          email: email,
          sessionId: sessionId || undefined
        });
      } else {
        // 尝试从localStorage获取最近的订单
        const lastOrder = localStorage.getItem('last_order');
        if (lastOrder) {
          try {
            const orderData = JSON.parse(lastOrder);
            if (orderData.productId === id) {
              setOrderInfo({
                orderId: orderData.orderId || orderData.sessionId,
                email: orderData.customerEmail,
                sessionId: orderData.sessionId
              });
            }
          } catch (e) {
            console.error('Error parsing order info:', e);
          }
        }
        
        // 如果没有订单信息，但用户已登录，设置用户邮箱并尝试验证购买状态
        if (userStr) {
          try {
            const userData = JSON.parse(userStr);
            setOrderInfo({ email: userData.email });
            
            if (userData.email) {
              productService.checkPurchaseStatus(id, userData.email)
                .then((result) => {
                  if (result.has_purchased) {
                    console.log('[下载页面] 验证购买状态成功:', result);
                    setOrderInfo((prev: OrderInfo | null) => ({
                      ...prev,
                      orderId: result.order_id,
                      verified: true
                    }));
                  } else {
                    console.log('[下载页面] 未找到购买记录');
                  }
                })
                .catch((error) => {
                  console.error('[下载页面] 验证购买状态失败:', error);
                });
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
      }
    }
  }, [id, fetchProduct, searchParams, navigate]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // 调用后端下载API - 修复API路径
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // 构建下载URL，包含验证信息
      let downloadUrl = `${API_BASE}/api/products/${id}/download`;
      const params = new URLSearchParams();
      
      // 所有产品都需要登录验证
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('需要登录才能下载产品');
      }
      
      try {
        const userData = JSON.parse(userStr);
        params.append('email', userData.email);
        
        // 如果是付费产品，还需要订单验证
        if (product && product.price > 0) {
          if (orderInfo && orderInfo.orderId) {
            params.append('order_id', orderInfo.orderId);
          }
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
        throw new Error('用户信息无效，请重新登录');
      }
      
      if (params.toString()) {
        downloadUrl += `?${params.toString()}`;
      }
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('下载失败');
      }
      
      // 获取文件名
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${product?.name}_训练计划.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // 创建下载链接
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // 显示成功消息
      alert('下载开始！如果下载没有自动开始，请检查您的浏览器设置。');
    } catch (err) {
      console.error('Download error:', err);
      alert('下载失败，请稍后重试');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">产品不存在</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
          >
            返回产品列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 成功购买横幅 */}
      <div className="bg-green-50 border-b border-green-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                {product.price === 0 
                  ? '免费产品！您现在可以下载您的训练计划了。' 
                  : '购买成功！您现在可以下载您的训练计划了。'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* 产品信息 */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-start space-x-6">
              <img
                src={product.images[0] || '/placeholder-image.jpg'}
                alt={product.name}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
                <p className="text-gray-600 mb-4">{product.description}</p>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>{product.price === 0 ? '获取时间' : '购买时间'}: {new Date().toLocaleDateString('zh-CN')}</span>
                  <span>|</span>
                  <span>订单号: {Math.random().toString(36).substr(2, 9).toUpperCase()}</span>
                  {product.price === 0 && (
                    <>
                      <span>|</span>
                      <span className="text-green-600 font-medium">免费产品</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 下载区域 */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">下载您的训练计划</h2>
            
            <div className="text-center">
              {/* Excel训练计划 */}
              <div className="border border-gray-200 rounded-lg p-8 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">完整训练计划</h3>
                <p className="text-sm text-gray-600 mb-6">包含详细的训练安排、进度跟踪和营养建议</p>
                {/* 订单验证状态 */}
                {product && product.price > 0 && (
                  <div className="mb-4">
                    {orderInfo && (orderInfo.orderId || orderInfo.sessionId || orderInfo.verified) ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-green-800 text-sm font-medium">支付已完成，可以下载</span>
                        </div>
                        {orderInfo.sessionId && (
                          <div className="mt-1 text-xs text-green-700">
                            会话ID: {orderInfo.sessionId.substring(0, 20)}...
                          </div>
                        )}
                        {orderInfo.orderId && (
                          <div className="mt-1 text-xs text-green-700">
                            订单ID: {orderInfo.orderId}
                          </div>
                        )}
                      </div>
                    ) : orderInfo && orderInfo.email ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-blue-800 text-sm font-medium">正在验证购买状态...</span>
                        </div>
                        <div className="mt-1 text-xs text-blue-700">
                          正在检查您的购买记录，请稍候...
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <span className="text-yellow-800 text-sm font-medium">需要购买此产品才能下载</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 text-lg"
                >
                  {downloading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      下载中...
                    </div>
                  ) : (
                    '下载训练计划 (Excel)'
                  )}
                </button>
              </div>
            </div>

            {/* 下载说明 */}
            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">下载说明</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>文件可以重复下载，无需额外费用</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>建议使用电脑下载以获得最佳体验</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>如果下载遇到问题，请检查网络连接或联系客服</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Excel文件包含多个工作表，请仔细查看所有内容</span>
                </li>
              </ul>
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="mt-8 flex justify-center space-x-4">
            <button
              onClick={() => navigate('/products')}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              浏览更多产品
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Download; 
